import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { Band, BandMember } from '@/types'

export const useBands = (userId: string | undefined) => {
  const [bands, setBands] = useState<Band[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchBands = useCallback(async () => {
    if (!userId) return
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('bands')
        .select('*')
        .order('created_at', { ascending: false })
      if (error) throw error
      setBands(data ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch bands')
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    void fetchBands()
  }, [fetchBands])

  const createBand = async (name: string) => {
    const { data, error } = await supabase
      .from('bands')
      .insert({ name, user_id: userId })
      .select()
      .single()
    if (error) throw error
    await fetchBands()
    return data as Band
  }

  const updateBand = async (id: string, name: string) => {
    const { error } = await supabase.from('bands').update({ name }).eq('id', id)
    if (error) throw error
    await fetchBands()
  }

  const deleteBand = async (id: string) => {
    const { error } = await supabase.from('bands').delete().eq('id', id)
    if (error) throw error
    await fetchBands()
  }

  return { bands, loading, error, refetch: fetchBands, createBand, updateBand, deleteBand }
}

export const useBandMembers = (bandId: string | undefined) => {
  const [members, setMembers] = useState<BandMember[]>([])
  const [loading, setLoading] = useState(true)

  const fetchMembers = useCallback(async () => {
    if (!bandId) return
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('band_members')
        .select('*')
        .eq('band_id', bandId)
        .order('name')
      if (error) throw error
      setMembers(data ?? [])
    } finally {
      setLoading(false)
    }
  }, [bandId])

  useEffect(() => {
    void fetchMembers()
  }, [fetchMembers])

  const addMember = async (member: { name: string; email: string; role: string }) => {
    const { error } = await supabase.from('band_members').insert({ ...member, band_id: bandId })
    if (error) throw error
    await fetchMembers()
  }

  const updateMember = async (id: string, updates: Partial<Pick<BandMember, 'name' | 'email' | 'role'>>) => {
    const { error } = await supabase.from('band_members').update(updates).eq('id', id)
    if (error) throw error
    await fetchMembers()
  }

  const removeMember = async (id: string) => {
    const { error } = await supabase.from('band_members').delete().eq('id', id)
    if (error) throw error
    await fetchMembers()
  }

  return { members, loading, refetch: fetchMembers, addMember, updateMember, removeMember }
}
